import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Upload as UploadIcon,
  ArrowRight,
  ShieldCheck,
  Truck,
  Headset,
  X as CloseIcon,
} from "lucide-react";
import Footer from "../components/Footer";
import TestimonialsSection from "../components/TestimonialsSection";
import ProductPicker from "../components/ProductPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MICRO_STEPS = [
  { num: "01", title: "Upload", note: "Your pet's clearest photo" },
  { num: "02", title: "We craft", note: "Hand-finished mockup" },
  { num: "03", title: "We ship", note: "Dispatch in 1 day" },
  { num: "04", title: "Delivered", note: "3–5 business days" },
];

const REASSURE = [
  { Icon: ShieldCheck, label: "Secure payments" },
  { Icon: Truck, label: "Pan-India delivery" },
  { Icon: Headset, label: "Quick support" },
];

/**
 * PromoPage — Meta-ad landing route at /promo/:slug.
 * Hero (with explicit MRP→sale pricing) + 4-step micro strip + reassurance
 * row + style picker + inline upload + How It Works. The header / hero CTAs
 * open the same upload form as a modal dialog so the customer never has to
 * scroll on click. Inline + dialog share parent state — switching between
 * them preserves picked file and pet name.
 */
const PromoPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const uploadRef = useRef(null);

  // Promo + catalog
  const [promo, setPromo] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [selectedStyleId, setSelectedStyleId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Upload form state (shared between inline + dialog)
  const [file, setFile] = useState(null);
  const [petName, setPetName] = useState("");
  const [uploading, setUploading] = useState(false);

  // Dialog open state
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/promo/${slug}`).then((r) => r.data.promo),
      axios.get(`${API}/styles/catalog`).then((r) => r.data.collections || []),
    ])
      .then(([p, cols]) => {
        if (cancel) return;
        setPromo(p);
        setCatalog(cols);
        const target = (cols || []).find((c) => c.id === p.collection_id);
        const styles = (target?.styles || []).filter(
          (s) => s.active !== false && !s.coming_soon
        );
        const chosen =
          styles.find((s) => s.id === p.default_style_id) || styles[0];
        if (chosen) setSelectedStyleId(chosen.id);
      })
      .catch(() => {
        if (!cancel) setPromo(null);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [slug]);

  const collectionStyles = useMemo(() => {
    if (!promo || !catalog) return [];
    const col = catalog.find((c) => c.id === promo.collection_id);
    return (col?.styles || []).filter((s) => s.active !== false);
  }, [promo, catalog]);

  // ----- Pricing — derived from catalog so admin changes flow through. -----
  // Customers can pick between A4 / 8×8 / 8×10 / 10×10 right inside the
  // upload form; the displayed sale price tracks `selectedSize`.

  // Available sizes (loaded once)
  const [sizes, setSizes] = useState([]);
  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/sizes`)
      .then((res) => {
        if (!cancel) setSizes(res.data?.items || []);
      })
      .catch(() => {
        if (!cancel) setSizes([]);
      });
    return () => {
      cancel = true;
    };
  }, []);

  // Selected size — defaults to the product flagged is_default in the DB,
  // or falls back to the promo's preferred size, or A4.
  const [selectedSize, setSelectedSize] = useState("a4");
  useEffect(() => {
    if (sizes && sizes.length > 0) {
      const def = sizes.find((s) => s.is_default);
      if (def) {
        setSelectedSize(def.id);
        return;
      }
    }
    if (promo?.default_size_id) setSelectedSize(promo.default_size_id);
  }, [promo?.default_size_id, sizes]);

  // Coupon discount fetched lazily; falls back to 500 (matches default SNUGGLE500).
  const [couponDiscount, setCouponDiscount] = useState(null);
  useEffect(() => {
    if (!promo?.coupon_code) return;
    let cancel = false;
    axios
      .post(`${API}/coupons/validate`, { code: promo.coupon_code })
      .then((res) => {
        if (cancel) return;
        const d = res.data?.discount;
        if (d > 0) setCouponDiscount(d);
      })
      .catch(() => {
        /* fall back to default below */
      });
    return () => {
      cancel = true;
    };
  }, [promo?.coupon_code]);

  const pricing = useMemo(() => {
    const col = catalog?.find((c) => c.id === promo?.collection_id);
    const sizedPrice = col?.prices?.[selectedSize];
    const fallback =
      typeof col?.price === "number" && col.price > 0 ? col.price : 1399;
    const mrp = typeof sizedPrice === "number" && sizedPrice > 0 ? sizedPrice : fallback;
    const discount = couponDiscount !== null ? couponDiscount : 500;
    const sale = Math.max(1, mrp - discount);
    return { mrp, discount, sale };
  }, [catalog, promo, couponDiscount, selectedSize]);

  // Per-size price map used by the ProductPicker for "from ₹X" labels and
  // per-card prices. Falls back to the collection's legacy single price.
  const catalogPricing = useMemo(() => {
    const col = catalog?.find((c) => c.id === promo?.collection_id);
    const fallback =
      typeof col?.price === "number" && col.price > 0 ? col.price : 1399;
    const map = {};
    (sizes || []).forEach((s) => {
      map[s.id] = (col?.prices && col.prices[s.id]) || fallback;
    });
    return map;
  }, [catalog, promo, sizes]);

  // Hero pricing — "starts at" reflects the cheapest active product across
  // the catalog (after the promo coupon discount applies). Updates as the
  // admin adds new products or tweaks per-size prices.
  const startsAtPricing = useMemo(() => {
    if (!sizes || sizes.length === 0 || !catalogPricing) return null;
    const bases = sizes
      .map((s) => catalogPricing[s.id])
      .filter((n) => typeof n === "number" && n > 0);
    if (bases.length === 0) return null;
    const mrp = Math.min(...bases);
    const discount = couponDiscount !== null ? couponDiscount : 500;
    return { mrp, discount, sale: Math.max(1, mrp - discount) };
  }, [sizes, catalogPricing, couponDiscount]);

  const promoCoupon = useMemo(
    () =>
      promo?.coupon_code
        ? { code: promo.coupon_code, discount: couponDiscount ?? 500 }
        : null,
    [promo, couponDiscount]
  );

  // ----- Upload helpers -----
  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10 MB)");
      return;
    }
    setFile(f);
  };

  // Pet name is only relevant for the Minimalist collection (which paints
  // the pet's name onto the artwork). Renaissance / IPL portraits don't use
  // the name, so we don't ask for it on those promo pages.
  const requiresPetName = (promo?.collection_id || "") === "minimalist";

  const trimmedName = (petName || "").trim();
  const formReady =
    !!file &&
    (!requiresPetName || !!trimmedName) &&
    !!selectedStyleId &&
    !!selectedSize &&
    !uploading;

  // Friendly explanation of the disabled CTA so the user knows what to fix.
  const cantSubmitReason = useMemo(() => {
    if (uploading) return null;
    if (!file && requiresPetName && !trimmedName)
      return "Upload a photo and enter your pet's name to continue";
    if (!file) return "Upload your pet's photo to continue";
    if (requiresPetName && !trimmedName) return "Enter your pet's name to continue";
    if (!selectedStyleId) return "Pick a style above";
    if (!selectedSize) return "Pick a size";
    return null;
  }, [file, trimmedName, selectedStyleId, selectedSize, uploading, requiresPetName]);

  // ----- Image strip data: filter gallery_images by promo collection + style -----
  // Mirror the StylePage rule so admins manage ONE set of images that drives
  // both surfaces: pull common + <collection>.common + <collection>.<style>.
  const selectedVariantData = useMemo(
    () => (sizes || []).find((s) => s.id === selectedSize) || {},
    [sizes, selectedSize]
  );
  const variantImages = useMemo(() => {
    const out = [];
    const g = selectedVariantData.gallery_images || {};
    if (Array.isArray(g)) {
      g.forEach((u) => u && out.push(u));
    } else {
      // Order: style-specific → collection-wide → universal common.
      // Most relevant on top, generic at the end.
      const cid = promo?.collection_id;
      if (cid) {
        const cBucket = g[cid];
        if (Array.isArray(cBucket)) {
          cBucket.forEach((u) => u && out.push(u));
        } else if (cBucket && typeof cBucket === "object") {
          if (selectedStyleId && Array.isArray(cBucket[selectedStyleId])) {
            cBucket[selectedStyleId].forEach((u) => u && out.push(u));
          }
          (cBucket.common || []).forEach((u) => u && out.push(u));
        }
      }
      (g.common || []).forEach((u) => u && out.push(u));
    }
    if (out.length === 0) {
      const sel =
        (collectionStyles || []).find((s) => s.id === selectedStyleId) || {};
      if (sel.thumbnail_url) out.push(sel.thumbnail_url);
    }
    return out;
  }, [selectedVariantData, promo, selectedStyleId, collectionStyles]);

  const onSubmit = async () => {
    if (!formReady) {
      toast.error(cantSubmitReason || "Please complete the form");
      return;
    }
    setUploading(true);
    setDialogOpen(false); // close dialog so the user sees the polling toast/state
    try {
      const form = new FormData();
      form.append("file", file);
      const up = await axios.post(`${API}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const imageUrl = up.data.image_url;
      const originalUrl = up.data.original_url || "";

      if (promo?.coupon_code) {
        try {
          sessionStorage.setItem("tsp_promo_coupon", promo.coupon_code);
        } catch {
          /* ignore */
        }
      }

      const start = await axios.post(
        `${API}/generate-styles`,
        {
          image_url: originalUrl || imageUrl,
          original_url: originalUrl,
          pet_name: trimmedName || "Pet",
          category: promo.collection_id,
          style_id: selectedStyleId,
          size_id: selectedSize,
        },
        { timeout: 30000 }
      );
      const jobId = start.data.job_id;

      let result = null;
      for (let i = 0; i < 80; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const poll = await axios.get(`${API}/generate-styles/${jobId}`);
        if (poll.data.status === "completed") {
          result = poll.data;
          break;
        } else if (poll.data.status === "failed") {
          throw new Error(poll.data.error || "Generation failed");
        }
      }
      if (!result) throw new Error("Generation timed out. Please try again.");

      navigate("/styles", {
        state: {
          originalImage: originalUrl || "",
          styles: result.styles,
          petName: trimmedName,
          category: promo.collection_id,
          styleId: selectedStyleId,
          selectedSize: selectedSize,
          couponCode: promo.coupon_code || null,
        },
      });
    } catch (err) {
      toast.error(
        err.response?.data?.detail || err.message || "Something went wrong"
      );
    } finally {
      setUploading(false);
    }
  };

  // ESC closes dialog
  useEffect(() => {
    if (!dialogOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDialogOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [dialogOpen]);

  const openUploadDialog = (e) => {
    e?.preventDefault();
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-[#C8A27A]" />
      </div>
    );
  }
  if (!promo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center px-6">
          <p className="text-sm text-muted-foreground mb-4">
            This promo is not available.
          </p>
          <Link to="/" className="btn-accent px-5 py-2 text-sm">
            Go to homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="promo-page">
      {/* Header */}
      <header className="glass-header py-5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Link
            to="/"
            className="serif text-2xl tracking-tight text-[#1A1A1A] font-semibold"
          >
            theSnugglePrints
          </Link>
          <button
            type="button"
            onClick={openUploadDialog}
            className="hidden sm:inline-flex btn-accent text-sm py-2.5 px-5"
            data-testid="promo-header-cta"
          >
            Upload pet photo &amp; see your portrait
          </button>
        </div>
      </header>

      <main className="pt-20 sm:pt-24">
        {/* ============================================================
            MOBILE-OPTIMIZED FLOW (also used on desktop, max-width capped)
            Order top → bottom:
              1. Compact offer header (title · price · discount on one line)
              2. Style selector
              3. Product selector
              4. Preview image + thumbnails  ← updates instantly on change
              5. Live price summary
              6. CTA
            The selectors are intentionally ABOVE the image so customers
            on Meta Ads traffic don't have to scroll up after toggling a
            product/style to see the preview change.
           ============================================================ */}
        <section
          className="px-4 sm:px-6 lg:px-10 max-w-3xl mx-auto pt-2 pb-2"
          data-testid="promo-condensed-header"
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1
              className="serif text-xl sm:text-2xl lg:text-3xl leading-tight text-[#1A1A1A]"
              data-testid="promo-headline"
            >
              {/* Use just the product name — strip the trailing discount
                  tag from the legacy "Foo Portraits — ₹500 OFF" headline
                  so the discount can be its own pill on the right. */}
              {(promo.headline || "").split(/[–—-]/)[0].trim() || promo.headline}
            </h1>
            <span
              className="text-sm sm:text-base text-[#1A1A1A]"
              data-testid="promo-price-onwards"
            >
              <span className="font-semibold">
                ₹{(startsAtPricing || pricing).sale.toLocaleString("en-IN")}
              </span>
              <span className="text-[#6B6B6B] ml-1">onwards</span>
            </span>
            <span className="inline-flex items-center bg-[#FAF6EF] text-[#9B6A2D] text-xs font-medium px-2 py-0.5 rounded-full">
              Flat ₹{(startsAtPricing || pricing).discount.toLocaleString("en-IN")} OFF
            </span>
          </div>
        </section>

        <section
          className="px-4 sm:px-6 lg:px-10 max-w-3xl mx-auto pb-8"
          data-testid="promo-mobile-flow"
        >
          {/* Step 1 — small style thumbnails */}
          {collectionStyles && collectionStyles.length > 0 && (
            <div className="mt-4">
              <PromoStyleThumbStrip
                styles={collectionStyles}
                selectedStyleId={selectedStyleId}
                onSelect={setSelectedStyleId}
              />
            </div>
          )}

          {/* Step 2 — flat tab picker (one row, scrolls horizontally on
              very narrow viewports). Description for the active tab lives
              right below the row so the customer always sees what they
              picked. Replaces the older 2×2 ProductPicker + frame-size
              sub-picker so frame sizes are first-class tabs (A4 vs 8×10). */}
          {sizes && sizes.length > 0 && (
            <div className="mt-4" data-testid="promo-product-section">
              <p className="label-uppercase mb-2 text-[#C8A27A] text-xs">
                Step 2 — Choose your product
              </p>
              <VariantTabPicker
                products={sizes}
                selectedId={selectedSize}
                onSelect={setSelectedSize}
              />
            </div>
          )}

          {/* Preview area — re-keyed on style+size so the thumbnail strip
              resets to image #0 on every selection change. Caps the height
              so the strip + CTA stay in the same viewport on mobile. */}
          <div className="mt-5" data-testid="promo-preview-area">
            <PromoVariantHero
              key={`${selectedStyleId}-${selectedSize}`}
              images={variantImages}
              alt={
                (collectionStyles.find((s) => s.id === selectedStyleId) || {})
                  .name || promo.headline
              }
            />
          </div>

          {/* Live price summary — sits right above the CTA. */}
          {pricing && (
            <div
              className="mt-5 flex items-baseline justify-center gap-2"
              data-testid="promo-selected-product-price"
            >
              <span className="serif text-2xl text-[#1A1A1A]">
                ₹{pricing.sale.toLocaleString("en-IN")}
              </span>
              <span className="text-sm text-[#9A9A9A] line-through">
                ₹{pricing.mrp.toLocaleString("en-IN")}
              </span>
              <span className="text-xs text-[#5A8C5A]">
                Flat ₹{pricing.discount.toLocaleString("en-IN")} OFF
              </span>
            </div>
          )}

          {/* Inline upload section — replaces the modal. The customer sees
              their file dropzone right under the preview, then commits with
              the "Create portrait" CTA below. */}
          <div className="mt-6" data-testid="promo-inline-upload" ref={uploadRef}>
            <p className="label-uppercase mb-2 text-[#C8A27A] text-xs">
              Step 3 — Upload your photo
            </p>
            <UploadForm
              file={file}
              onFileChange={onFileChange}
              petName={petName}
              setPetName={setPetName}
              requiresPetName={requiresPetName}
              sizes={sizes}
              selectedSize={selectedSize}
              setSelectedSize={setSelectedSize}
              pricing={pricing}
              catalogPricing={catalogPricing}
              promoCoupon={promoCoupon}
              uploading={uploading}
              onSubmit={onSubmit}
              formReady={formReady}
              cantSubmitReason={cantSubmitReason}
              keyPrefix="inline"
            />
          </div>

          {/* Reassurance row */}
          <ul
            className="flex items-center justify-center flex-wrap gap-x-5 gap-y-2 mt-5"
            data-testid="promo-reassurance"
          >
            {REASSURE.map(({ Icon, label }) => (
              <li
                key={label}
                className="flex items-center gap-2 text-xs text-[#6B6B6B]"
              >
                <Icon className="h-4 w-4 text-[#C8A27A]" />
                {label}
              </li>
            ))}
          </ul>
        </section>

        {/* Sticky bottom CTA — smart label.
              · No file uploaded → "Upload image" : scrolls the customer to
                the inline upload section above so they can pick a file.
              · File uploaded → "Create portrait" : triggers the same
                onSubmit the in-form button calls (generation + redirect).
            Hidden once the customer reaches the upload area so it doesn't
            duplicate the in-form button. */}
        <div
          className="sticky bottom-0 inset-x-0 z-40 bg-card border-t border-[#EAE5DF] px-4 py-3 sm:hidden"
          data-testid="promo-sticky-cta"
        >
          <button
            type="button"
            onClick={() => {
              if (!file) {
                uploadRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              } else if (formReady && !uploading) {
                onSubmit();
              }
            }}
            disabled={uploading || (file && !formReady)}
            className="btn-accent w-full h-12 inline-flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="promo-sticky-cta-button"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Crafting your portrait…
              </>
            ) : file ? (
              <>
                Create portrait
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <>
                Upload image
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>

        {/* Micro strip — 4 quick steps below the hero */}
        <section
          className="border-y border-[#EAE5DF] bg-[#FAF6F0]"
          data-testid="promo-micro-strip"
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6">
              {MICRO_STEPS.map((s) => (
                <div key={s.num} className="flex items-start gap-3">
                  <span className="serif text-2xl text-[#C8A27A] leading-none mt-0.5">
                    {s.num}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-[#1A1A1A] leading-tight">
                      {s.title}
                    </p>
                    <p className="text-xs text-[#6B6B6B] leading-snug mt-0.5">
                      {s.note}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works — detailed */}
        <section
          className="px-6 lg:px-10 max-w-6xl mx-auto py-16 border-t border-[#EAE5DF]"
          data-testid="promo-how-it-works"
        >
          <h2 className="serif text-3xl sm:text-4xl text-[#1A1A1A] text-center mb-10">
            How it works
          </h2>
          <div className="grid sm:grid-cols-3 gap-8">
            <HowStep
              num="1"
              title="Upload"
              desc="Pick a clear, well-lit photo of your pet. Eyes visible, front-facing works best."
            />
            <HowStep
              num="2"
              title="We craft"
              desc="Our artists hand-finish a premium A4 framed portrait, ready to hang."
            />
            <HowStep
              num="3"
              title="Doorstep delivery"
              desc="Dispatched within 1 business day. Pan-India delivery in 3–5 business days."
            />
          </div>
        </section>

        {/* Testimonials — admin-managed; only renders if any are active */}
        <TestimonialsSection />
      </main>

      <Footer />

      {/* Sticky mobile CTA */}
      <div className="h-20 lg:hidden" aria-hidden="true"></div>
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pt-3 pb-4 bg-background/95 backdrop-blur-sm border-t border-[#EAE5DF]">
        <button
          type="button"
          onClick={openUploadDialog}
          className="btn-accent w-full py-3 text-sm"
          data-testid="promo-mobile-cta"
        >
          Upload pet photo &amp; see your portrait
        </button>
      </div>

      {/* Upload Dialog — same form as inline section */}
      {dialogOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={() => setDialogOpen(false)}
          data-testid="promo-upload-dialog"
        >
          <div
            className="bg-background w-full max-w-xl my-4 sm:my-0 max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#EAE5DF]">
              <h3 className="serif text-xl text-[#1A1A1A]">
                Upload your pet's photo
              </h3>
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="text-[#6B6B6B] hover:text-[#1A1A1A] p-1"
                aria-label="Close"
                data-testid="promo-dialog-close"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5">
              {/* Step 1 — Choose style (mini carousel) */}
              {collectionStyles && collectionStyles.length > 0 && (
                <div className="mb-5" data-testid="promo-dialog-styles">
                  <p className="label-uppercase mb-2 text-[#C8A27A] text-xs">
                    Step 1 — Choose your style
                  </p>
                  <PromoStyleCarousel
                    styles={collectionStyles}
                    selectedStyleId={selectedStyleId}
                    onSelect={setSelectedStyleId}
                  />
                </div>
              )}

              {/* Step 2 — Choose product */}
              {sizes && sizes.length > 0 && (
                <div className="mb-5" data-testid="promo-dialog-product">
                  <p className="label-uppercase mb-2 text-[#C8A27A] text-xs">
                    Step 2 — Choose your product
                  </p>
                  <ProductPicker
                    products={sizes}
                    selectedId={selectedSize}
                    onSelect={setSelectedSize}
                    priceFor={(id) => {
                      const base =
                        (catalogPricing && catalogPricing[id]) || pricing?.mrp || 1399;
                      const final = Math.max(1, base - (pricing?.discount || 0));
                      return { base, final };
                    }}
                    coupon={promoCoupon}
                    compact
                  />
                </div>
              )}

              {/* Step 3 — Upload */}
              <p className="label-uppercase mb-2 text-[#C8A27A] text-xs">
                Step 3 — Upload your photo
              </p>
              <p className="text-xs text-[#6B6B6B] mb-3">
                Takes less than 1 minute. Clear phone photos work best.
              </p>
              <UploadForm
                file={file}
                onFileChange={onFileChange}
                petName={petName}
                setPetName={setPetName}
                requiresPetName={requiresPetName}
                sizes={sizes}
                selectedSize={selectedSize}
                setSelectedSize={setSelectedSize}
                pricing={pricing}
                catalogPricing={catalogPricing}
                promoCoupon={promoCoupon}
                uploading={uploading}
                onSubmit={onSubmit}
                formReady={formReady}
                cantSubmitReason={cantSubmitReason}
                keyPrefix="dialog"
              />
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen generation overlay — blocks interaction and reassures
          the customer that their portrait is being crafted. Shown while the
          backend polls (~60–90s). */}
      {uploading && <GeneratingOverlay petName={(petName || "").trim()} />}
    </div>
  );
};

const GeneratingOverlay = ({ petName }) => (
  <div
    className="fixed inset-0 z-[60] bg-[#1A1A1A]/85 backdrop-blur-md flex items-center justify-center p-6"
    role="dialog"
    aria-modal="true"
    aria-label="Crafting your portrait"
    data-testid="promo-generating-overlay"
  >
    <div className="text-center max-w-md w-full">
      {/* Animated ring */}
      <div className="relative w-20 h-20 mx-auto mb-7" aria-hidden="true">
        <div className="absolute inset-0 rounded-full border-2 border-[#C8A27A]/25"></div>
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#C8A27A] animate-spin"></div>
        <div className="absolute inset-3 rounded-full border border-[#C8A27A]/20"></div>
      </div>
      <p className="label-uppercase text-[#C8A27A] mb-3">
        Crafting your portrait
      </p>
      <h3 className="serif text-2xl sm:text-3xl text-white leading-tight">
        {petName
          ? `Painting ${petName}, please don't close this page`
          : "Painting your pet, please don't close this page"}
      </h3>
      <p className="text-sm text-white/70 mt-4 leading-relaxed">
        This usually takes about 60 seconds. You'll see your framed portrait
        as soon as it's ready.
      </p>
      <ul className="mt-7 flex items-center justify-center gap-5 text-[11px] text-white/60">
        <li className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#C8A27A] animate-pulse"></span>
          Studying your photo
        </li>
        <li className="flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full bg-[#C8A27A] animate-pulse"
            style={{ animationDelay: "300ms" }}
          ></span>
          Brushing details
        </li>
        <li className="flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full bg-[#C8A27A] animate-pulse"
            style={{ animationDelay: "600ms" }}
          ></span>
          Framing
        </li>
      </ul>
    </div>
  </div>
);

const SIZE_BLURB_PROMO = {
  a4: "Standard portrait",
  "8x8": "Square — shelves & gallery walls",
  "8x10": "Most popular",
  "10x10": "Statement square",
};

const UploadForm = ({
  file,
  onFileChange,
  petName,
  setPetName,
  requiresPetName,
  sizes,
  selectedSize,
  setSelectedSize,
  pricing,
  catalogPricing,
  promoCoupon,
  uploading,
  onSubmit,
  formReady,
  cantSubmitReason,
  keyPrefix,
}) => (
  <>
    <label
      htmlFor={`${keyPrefix}-upload-input`}
      className={`block border-2 border-dashed border-[#D9D3CA] rounded-sm p-7 sm:p-10 text-center cursor-pointer hover:border-[#C8A27A] transition-colors bg-card ${
        file ? "border-[#C8A27A] bg-[#FFF7E6]/40" : ""
      }`}
    >
      <input
        id={`${keyPrefix}-upload-input`}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFileChange}
        data-testid={`promo-upload-input-${keyPrefix}`}
      />
      {file ? (
        <div>
          <p className="serif text-lg text-[#1A1A1A] mb-1">{file.name}</p>
          <p className="text-xs text-[#6B6B6B]">
            {(file.size / 1024 / 1024).toFixed(2)} MB · click to replace
          </p>
        </div>
      ) : (
        <div>
          <UploadIcon className="mx-auto h-7 w-7 text-[#C8A27A] mb-3" />
          <p className="serif text-lg text-[#1A1A1A]">
            Drop your pet photo here
          </p>
          <p className="text-xs text-[#6B6B6B] mt-1">or click to browse</p>
        </div>
      )}
    </label>

    {/* Pet name input — only for collections that paint the name (Minimalist) */}
    {requiresPetName && (
      <div className="mt-5">
        <label className="label-uppercase block mb-2 text-[#6B6B6B]">
          Pet&apos;s name <span className="text-red-700">*</span>
        </label>
        <input
          type="text"
          value={petName}
          onChange={(e) => setPetName(e.target.value)}
          maxLength={24}
          placeholder="e.g. Buddy"
          required
          className="w-full border border-[#D9D3CA] bg-card px-4 py-3 text-sm focus:outline-none focus:border-[#C8A27A]"
          data-testid={`promo-pet-name-${keyPrefix}`}
        />
      </div>
    )}

    <button
      type="button"
      onClick={onSubmit}
      disabled={!formReady}
      className="btn-accent w-full mt-5 py-4 text-base inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
      data-testid={`promo-submit-${keyPrefix}`}
    >
      {uploading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Crafting your portrait…
        </>
      ) : (
        <>
          {keyPrefix === "inline" ? "Create portrait" : "Upload pet photo & create portrait"}
          <ArrowRight className="h-4 w-4" />
        </>
      )}
    </button>

    {/* Why is this disabled? — surfaces the exact missing field. */}
    {!formReady && !uploading && cantSubmitReason && (
      <p
        className="text-center text-xs text-[#9B6A2D] mt-2"
        data-testid={`promo-submit-hint-${keyPrefix}`}
      >
        {cantSubmitReason}
      </p>
    )}
  </>
);

/**
 * Single-row style carousel for the promo page. Mirrors the homepage
 * CollectionRow — fixed-width cards, scroll-snap, no wrap at any breakpoint.
 * "Coming soon" cards remain visible but are disabled.
 */
const PromoStyleCarousel = ({ styles, selectedStyleId, onSelect }) => {
  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.8) * (dir === "left" ? -1 : 1);
    el.scrollBy({ left: amount, behavior: "smooth" });
  };
  return (
    <div>
      {/* Desktop scroll controls — keep the carousel single-row no matter how
          many styles a collection has. Placed inline above the carousel so
          they don't collide with the section header on either side. */}
      <div className="hidden md:flex items-center justify-end gap-1 mb-2">
        <button
          type="button"
          onClick={() => scrollBy("left")}
          aria-label="Scroll styles left"
          className="w-8 h-8 border border-[#EAE5DF] hover:border-[#C8A27A] hover:bg-[#FAF6EF] transition-colors flex items-center justify-center"
          data-testid="promo-style-scroll-left"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#1A1A1A]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <button
          type="button"
          onClick={() => scrollBy("right")}
          aria-label="Scroll styles right"
          className="w-8 h-8 border border-[#EAE5DF] hover:border-[#C8A27A] hover:bg-[#FAF6EF] transition-colors flex items-center justify-center"
          data-testid="promo-style-scroll-right"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#1A1A1A]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 md:mx-0 md:px-0"
        style={{ scrollSnapType: "x mandatory" }}
        data-testid="promo-style-carousel"
      >
        {styles.map((s) => {
          const selected = s.id === selectedStyleId;
          const disabled = !!s.coming_soon;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => !disabled && onSelect(s.id)}
              disabled={disabled}
              data-testid={`promo-style-${s.id}`}
              className={`flex-shrink-0 w-44 sm:w-52 md:w-56 lg:w-60 scroll-snap-start bg-card border transition-all flex flex-col text-left overflow-hidden ${
                selected
                  ? "border-[#C8A27A] ring-2 ring-[#C8A27A]/30"
                  : "border-[#EAE5DF] hover:border-[#C8A27A]/60"
              } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <div className="aspect-[4/5] bg-[#F3EEE6] flex items-center justify-center relative">
                {s.thumbnail_url ? (
                  <img
                    src={s.thumbnail_url}
                    alt={s.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="serif text-[#9B6A2D] text-base px-3 text-center">
                    {s.name}
                  </span>
                )}
                {disabled && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <span className="bg-white/95 text-[#1A1A1A] text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full">
                      Coming soon
                    </span>
                  </div>
                )}
              </div>
              <div className="px-3 py-3 flex items-center justify-between">
                <p className="text-sm font-medium text-[#1A1A1A] truncate">
                  {s.name}
                </p>
                {selected && (
                  <span className="text-[10px] bg-[#C8A27A] text-white px-1.5 py-0.5 rounded">
                    SELECTED
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const HowStep = ({ num, title, desc }) => (
  <div className="text-center">
    <div className="w-12 h-12 bg-[#FFF7E6] text-[#9B6A2D] rounded-full inline-flex items-center justify-center mb-4 serif text-lg font-bold">
      {num}
    </div>
    <h3 className="serif text-xl text-[#1A1A1A] mb-2">{title}</h3>
    <p className="text-sm text-[#6B6B6B] max-w-[260px] mx-auto">{desc}</p>
  </div>
);

/**
 * LEFT-column dynamic hero + thumbnail strip for the promo page. Pulls
 * images from `selectedVariant.gallery_images` filtered by the current
 * collection (always the promo's collection) + the customer's currently
 * picked style. Mirrors the StylePage VariantHero but lives in its own
 * component so the promo can iterate independently. The parent passes a
 * `keySig` prop and uses it as React `key` to remount this component
 * (resetting to image #0) whenever the customer switches style or size.
 */
const PromoVariantHero = ({ images, alt }) => {
  const [active, setActive] = useState(0);
  if (!images || images.length === 0) {
    return (
      <div
        className="bg-card border border-[#EAE5DF] aspect-square sm:aspect-[4/5] overflow-hidden flex items-center justify-center"
        data-testid="promo-variant-hero"
      >
        <span className="serif text-2xl text-[#6B6B6B] px-6 text-center">
          {alt || "Your portrait"}
        </span>
      </div>
    );
  }
  const safeIdx = Math.min(active, images.length - 1);
  return (
    <div className="flex flex-col gap-3" data-testid="promo-variant-hero">
      <div className="bg-card border border-[#EAE5DF] aspect-square sm:aspect-[4/5] overflow-hidden flex items-center justify-center">
        <img
          src={images[safeIdx]}
          alt={alt || "Pet portrait"}
          className="w-full h-full object-cover"
          key={images[safeIdx]}
          data-testid="promo-variant-hero-image"
        />
      </div>
      {images.length > 1 && (
        <div
          className="flex gap-2 overflow-x-auto pb-1"
          data-testid="promo-variant-hero-thumbs"
        >
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setActive(i)}
              className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-card border-2 overflow-hidden transition-colors ${
                i === safeIdx
                  ? "border-[#C8A27A]"
                  : "border-[#EAE5DF] hover:border-[#C8A27A]/60"
              }`}
              data-testid={`promo-variant-hero-thumb-${i}`}
              aria-label={`View image ${i + 1}`}
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Flat single-row variant tab selector. Used in the promo flow as Step 2:
 * every product variant (A4 frame, 8×10 frame, canvas, acrylic stand, mug)
 * is its own first-class tab so the customer doesn't have to drill through
 * a type → size sub-picker. A single-line description renders right below
 * the active tab so the chosen variant's dimensions stay visible at all
 * times.
 *
 * Order:
 *   1. Frame variants sorted by area ascending (A4 → 8×10 → larger).
 *   2. Canvas
 *   3. Acrylic stand
 *   4. Mug
 */
const VARIANT_TYPE_ORDER = ["frame", "canvas", "acrylic", "mug"];

const formatVariantTab = (v) => {
  // Short label shown inside the tab itself.
  if (v.product_type === "frame") {
    // A4 stays "A4 Frame"; everything else uses its name as-is (e.g. "8 × 10 Frame").
    const isA4 = /a4/i.test(v.name || v.id || "");
    if (isA4) return "A4 Frame";
    return `${(v.name || "").replace(/\s*inch.*$/i, "").trim()} Frame`;
  }
  if (v.product_type === "canvas") return "Canvas";
  if (v.product_type === "acrylic") return "Stand";
  if (v.product_type === "mug") return "Mug";
  return v.name || v.id;
};

const formatVariantDescription = (v) => {
  // Single-line description under the active tab.
  if (!v) return "";
  if (v.product_type === "mug") {
    const m = (v.name || "").match(/(\d+(?:\.\d+)?)\s*oz/i);
    const oz = m ? parseFloat(m[1]) : null;
    if (oz) return `${Math.round(oz * 29.5735)} ml coffee mug`;
    return `${v.name} coffee mug`;
  }
  const fmt = (n) =>
    typeof n !== "number"
      ? n
      : Number.isInteger(n)
        ? `${n}`
        : n.toFixed(1).replace(/\.0$/, "");
  if (v.product_type === "frame") {
    const isA4 = /a4/i.test(v.name || v.id || "");
    if (isA4) return "A4 size frame";
    if (v.width_inches && v.height_inches) {
      return `${fmt(v.width_inches)} × ${fmt(v.height_inches)} inch frame`;
    }
    return v.name || "Frame";
  }
  if (v.width_inches && v.height_inches) {
    const kind =
      v.product_type === "canvas"
        ? "canvas"
        : v.product_type === "acrylic"
          ? "acrylic stand"
          : v.product_type;
    return `${fmt(v.width_inches)} × ${fmt(v.height_inches)} inch ${kind}`;
  }
  return v.name || "";
};

const VariantTabPicker = ({ products, selectedId, onSelect }) => {
  // Build ordered list: frame variants by area, then canvas/acrylic/mug.
  const ordered = React.useMemo(() => {
    const buckets = {};
    (products || []).forEach((p) => {
      if (!p.active && p.active !== undefined) return;
      const t = p.product_type || "frame";
      (buckets[t] = buckets[t] || []).push(p);
    });
    // Frames: A4 first per spec, then any others by ascending area so 8×10
    // → 11×14 → etc follow in size order.
    if (buckets.frame) {
      buckets.frame.sort((a, b) => {
        const aA4 = /a4/i.test(a.name || a.id || "");
        const bA4 = /a4/i.test(b.name || b.id || "");
        if (aA4 !== bA4) return aA4 ? -1 : 1;
        return (
          (a.width_inches || 0) * (a.height_inches || 0) -
          (b.width_inches || 0) * (b.height_inches || 0)
        );
      });
    }
    const out = [];
    VARIANT_TYPE_ORDER.forEach((t) => {
      (buckets[t] || []).forEach((p) => out.push(p));
    });
    // Append any unknown product types at the end (defensive).
    Object.keys(buckets).forEach((t) => {
      if (!VARIANT_TYPE_ORDER.includes(t))
        buckets[t].forEach((p) => out.push(p));
    });
    return out;
  }, [products]);

  if (ordered.length === 0) return null;
  const active = ordered.find((p) => p.id === selectedId) || ordered[0];

  return (
    <div data-testid="variant-tab-picker">
      {/* Single row of tabs. Horizontal scroll if too many to fit. */}
      <div
        className="flex gap-2 overflow-x-auto pb-1"
        style={{ scrollSnapType: "x mandatory" }}
        role="tablist"
      >
        {ordered.map((v) => {
          const isActive = v.id === active.id;
          return (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(v.id)}
              className={`flex-shrink-0 scroll-snap-start px-3 py-2 text-sm border whitespace-nowrap transition-colors ${
                isActive
                  ? "border-[#C8A27A] bg-[#FAF6EF] text-[#1A1A1A]"
                  : "border-[#EAE5DF] bg-card text-[#1A1A1A]/80 hover:border-[#C8A27A]/60"
              }`}
              data-testid={`variant-tab-${v.id}`}
            >
              {formatVariantTab(v)}
            </button>
          );
        })}
      </div>
      <p
        className="text-xs text-[#6B6B6B] mt-2"
        data-testid="variant-tab-description"
      >
        {formatVariantDescription(active)}
      </p>
    </div>
  );
};

/**
 * Compact style thumbnail strip for the RIGHT column on the promo page —
 * smaller than the inline PromoStyleCarousel since it lives next to other
 * controls. Single row, horizontal scroll, no wrap. Disabled "coming soon"
 * styles remain visible.
 */
const PromoStyleThumbStrip = ({ styles, selectedStyleId, onSelect }) => (
  <div data-testid="promo-style-thumbs">
    <p className="label-uppercase mb-2 text-[#C8A27A] text-xs">
      Step 1 — Choose your style
    </p>
    <div
      className="flex gap-1.5 overflow-x-auto pb-1"
      style={{ scrollSnapType: "x mandatory" }}
    >
      {styles.map((s) => {
        const selected = s.id === selectedStyleId;
        const disabled = !!s.coming_soon;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => !disabled && onSelect(s.id)}
            disabled={disabled}
            className={`flex-shrink-0 scroll-snap-start w-16 sm:w-20 bg-card border-2 overflow-hidden transition-colors flex flex-col ${
              selected
                ? "border-[#C8A27A]"
                : "border-[#EAE5DF] hover:border-[#C8A27A]/60"
            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
            data-testid={`promo-style-thumb-${s.id}`}
          >
            <div className="aspect-square bg-[#F3EEE6] flex items-center justify-center relative">
              {s.thumbnail_url ? (
                <img
                  src={s.thumbnail_url}
                  alt={s.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="serif text-[#9B6A2D] text-[9px] px-1 text-center leading-tight">
                  {s.name}
                </span>
              )}
              {disabled && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="bg-white/95 text-[#1A1A1A] text-[7px] uppercase tracking-wide px-1 py-0.5 rounded-full">
                    Soon
                  </span>
                </div>
              )}
            </div>
            <p className="text-[9px] sm:text-[10px] px-1 py-1 text-[#1A1A1A] truncate text-center leading-tight">
              {s.name}
            </p>
          </button>
        );
      })}
    </div>
  </div>
);

/**
 * Hero product card on the promo page — admin uploads ONE image per promo
 * in /admin → Promo Pages (the "Right panel image" slot). Nothing is
 * derived from product variants/mockups; the admin has full control.
 */
const PromoHeroProductCard = ({ imageUrl, styleName }) => {
  return (
    <div
      className="bg-card border border-[#C8A27A] overflow-hidden"
      data-testid="promo-hero-product-card"
    >
      <div className="aspect-[4/5] bg-[#F3EEE6] flex items-center justify-center">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={styleName || "Pet portrait product"}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="serif text-[#9B6A2D] text-2xl px-6 text-center">
            Your portrait
          </span>
        )}
      </div>
    </div>
  );
};

export default PromoPage;
